import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SERVICES = ["conveyancing", "antenuptial", "trust", "other"];

export default function Templates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", service_type: "conveyancing", content: "" });
  const [show, setShow] = useState(null);

  const load = () => api.get("/templates").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      // Extract placeholders from content
      const placeholders = [...new Set([...form.content.matchAll(/\{(\w+)\}/g)].map((m) => m[1]))];
      await api.post("/templates", { ...form, placeholders });
      toast.success("Template saved");
      setOpen(false);
      setForm({ name: "", service_type: "conveyancing", content: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto" data-testid="admin-templates">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-overline text-zinc-500">Templates</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Document templates</h1>
          <p className="mt-2 text-sm text-zinc-600">
            Use <code className="bg-zinc-100 px-1 font-mono text-xs">{`{placeholder}`}</code> tokens — AI will fill these from matter data.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="new-template-btn">
          <Plus className="h-4 w-4 mr-2" /> New template
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {list.map((t) => (
          <div key={t.id} className="bg-white border border-zinc-200 p-6" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-overline text-zinc-500 mt-1">{t.service_type}</div>
              </div>
              <button onClick={() => remove(t.id)} className="text-zinc-400 hover:text-red-600">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {t.placeholders?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {t.placeholders.map((p) => (
                  <span key={p} className="text-[10px] font-mono bg-zinc-100 text-zinc-700 px-1.5 py-0.5">{p}</span>
                ))}
              </div>
            )}
            <button onClick={() => setShow(t)} className="mt-4 text-sm text-zinc-600 hover:text-zinc-900">
              Preview →
            </button>
          </div>
        ))}
        {list.length === 0 && (
          <div className="md:col-span-2 p-10 text-center text-zinc-500 bg-white border border-zinc-200">
            No templates yet.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">New template</DialogTitle>
            <DialogDescription>Use {`{client_full_name}`}, {`{purchase_price}`}, etc. as placeholders.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="tpl-name" />
            </div>
            <div>
              <Label>Service type</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={12}
                className="rounded-none font-mono text-xs"
                data-testid="tpl-content"
              />
            </div>
          </div>
          <Button onClick={save} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="tpl-save">
            Save template
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!show} onOpenChange={() => setShow(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{show?.name}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-xs bg-zinc-50 border border-zinc-200 p-6 font-mono leading-relaxed">{show?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
